/**
 * useTheme — manages light / dark / system theme preference.
 *
 * Preference is persisted to localStorage. On every call the hook
 * applies or removes the `dark` class on <html> so Tailwind's
 * darkMode: 'class' strategy takes effect globally.
 *
 * Usage:
 *   const { theme, setTheme, isDark } = useTheme()
 *
 *   theme    — 'light' | 'dark' | 'system'
 *   setTheme — (value: string) => void
 *   isDark   — boolean: whether the screen is currently dark
 */
import { useState, useEffect } from 'react'

const THEME_KEY = 'cg_theme'

function computeIsDark(preference) {
  if (preference === 'dark')  return true
  if (preference === 'light') return false
  // 'system' — follow the OS
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

function applyTheme(preference) {
  document.documentElement.classList.toggle('dark', computeIsDark(preference))
}

export function useTheme() {
  const [theme, setThemeState] = useState(() => {
    return localStorage.getItem(THEME_KEY) || 'system'
  })

  // Apply on mount + whenever preference changes
  useEffect(() => {
    applyTheme(theme)

    // When set to 'system', re-apply if the OS preference changes
    if (theme !== 'system') return
    const mq      = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => applyTheme('system')
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [theme])

  const setTheme = (newPref) => {
    localStorage.setItem(THEME_KEY, newPref)
    setThemeState(newPref)
  }

  return { theme, setTheme, isDark: computeIsDark(theme) }
}

// Apply theme immediately on module load (before React hydrates) to
// prevent a white flash when the user has dark mode set.
applyTheme(localStorage.getItem(THEME_KEY) || 'system')
